import { Injectable } from '@angular/core';
import { PlanSubscription } from 'app/entities/plan-subscription';
import { User } from 'app/entities/user';
import { environment } from 'environments/environment';

declare global {
    interface Window {
        clarity?: {
            (...args: any[]): void;
            q?: any[];
        };
    }
}

@Injectable({
    providedIn: 'root'
})
export class ClarityService {
    private scriptLoaded = false;
    private lastUserKey?: string;
    private lastSegmentKey?: string;

    load(): void {
        const projectId = environment.clarityProjectId;

        if (!projectId || this.scriptLoaded) {
            return;
        }

        window.clarity = window.clarity || function(): void {
            (window.clarity!.q = window.clarity!.q || []).push(arguments);
        };

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.clarity.ms/tag/${projectId}`;

        const firstScript = document.getElementsByTagName('script')[0];
        firstScript.parentNode!.insertBefore(script, firstScript);

        this.scriptLoaded = true;
    }

    identifyUser(user: User | null | undefined, subscriptionOverride?: PlanSubscription | null): void {
        if (!user?.id) {
            return;
        }

        this.load();

        const userKey = String(user.id);
        const account = user.account;
        const subscription = subscriptionOverride ?? account?.subscription;
        const segmentKey = JSON.stringify({
            userId: user.id,
            accountId: account?.id,
            plan: this.getPlanSlug(subscription),
            planStatus: this.getPlanStatus(subscription),
            role: user.roleName,
        });

        if (this.lastUserKey !== userKey) {
            window.clarity?.('identify', userKey, null, null, user.name ?? undefined);
            this.lastUserKey = userKey;
        }

        if (this.lastSegmentKey === segmentKey) {
            return;
        }

        this.set('plan', this.getPlanSlug(subscription));
        this.set('plan_status', this.getPlanStatus(subscription));
        this.set('account_id', account?.id);
        this.set('user_role', user.roleName);

        this.lastSegmentKey = segmentKey;
    }

    private set(key: string, value: string | number | null | undefined): void {
        if (value === null || value === undefined || value === '') {
            return;
        }

        window.clarity?.('set', key, String(value));
    }

    private getPlanSlug(subscription: PlanSubscription | null | undefined): string | undefined {
        const planPayment = subscription?.planPayment;

        return planPayment?.plan?.slug ?? planPayment?.slug;
    }

    private getPlanStatus(subscription: PlanSubscription | null | undefined): string | undefined {
        return subscription?.statusFormatted ?? subscription?.status?.toString();
    }
}
